import { useState } from "react";
import { ICaring } from "../../Models/ICaring";
import { IRole } from "../../Models/IRole";
import { ISentinelRich } from "../../Models/ISentinel";
import { IUser } from "../../Models/IUser";
import { ToastMessage } from "../../Utils/UIMessages";
import { dispName, emailsOf, kindOf, mailReason } from "./SetupHelpers";
import {
  Btn,
  CARDS_MAX_WIDTH,
  Chips,
  Dialog,
  Empty,
  ISetupColumn,
  PageHead,
  Pill,
  SetupCards,
  SetupTable,
  StateText,
  Toolbar,
} from "./SetupUi";
import UserDialog from "./UserDialog";

// Scheda Utenti (risposte 7, 9, 13): «Aggiungi utente» e «Modifica» aprono una finestra; la colonna
// «Assegnato a» (sola lettura) dice a quali dispositivi è assegnato ogni operatore: si cambia dal Dispositivo.

// oltre questi dispositivi la cella si ferma e «Tutti i N» apre l'elenco
const CARES_MAX = 12;

const UsersTab = (props: {
  width: number;
  users: IUser[];
  roles: IRole[];
  sentinels: ISentinelRich[];
  carings: ICaring[];
  loaded: boolean;
  meId?: number;
  reloadUsers: () => void;
  onRelogin: () => void;
}) => {
  const [filter, setFilter] = useState<string>("tutti");
  const [search, setSearch] = useState<string>("");
  const [editing, setEditing] = useState<IUser | null | undefined>(undefined);
  const [caresOf, setCaresOf] = useState<IUser | undefined>();

  const isMe = (u: IUser) => u.id === props.meId;
  const q = search.trim().toLowerCase();
  const rows = props.users
    .filter((u) => (filter === "tutti" || mailReason(u)) && (!q || [u.identifier, u.email].join(" ").toLowerCase().includes(q)))
    .sort((a, b) => a.identifier.localeCompare(b.identifier, "it", { numeric: true }));

  const devicesOf = (u: IUser) =>
    props.carings
      .filter((c) => c.userId === u.id)
      .map((c) => props.sentinels.find((s) => s.patient?.id === c.patientId))
      .filter((s): s is ISentinelRich => !!s && !s.logicalDeleted);

  const roleText = (u: IUser) => (u.roles ?? []).map((r) => r.type.toUpperCase()).join(", ") || "nessun ruolo";

  const mailCell = (u: IUser) => {
    const list = emailsOf(u);
    if (!list.length) return <span className="el muted">nessuna</span>;
    return (
      <span className="cellrow">
        <span className="el" title={u.email ?? ""}>
          {list[0]}
        </span>
        {list.length > 1 && <Pill kind="grey" text={"+" + (list.length - 1)} title={list.slice(1).join(", ")} />}
      </span>
    );
  };

  const mailPill = (u: IUser) => {
    if (!u.email) return <Pill kind="warn" text="Manca l'email" />;
    if (!u.enabled) return <Pill kind="grey" text="No: utente spento" />;
    return u.emailNotifications !== false ? <Pill kind="ok" text="Sì" /> : <Pill kind="grey" text="Spente" />;
  };

  const caresCell = (u: IUser) => {
    const list = devicesOf(u).map(dispName);
    if (!list.length) return <span className="el muted">nessuno</span>;
    return (
      <span className="cares">
        <span className="clamp2" title={list.join(", ")}>
          {list.slice(0, CARES_MAX).join(", ")}
          {list.length > CARES_MAX ? ", ..." : ""}
        </span>
        {list.length > CARES_MAX && <Btn text={"Tutti i " + list.length} small onClick={() => setCaresOf(u)} />}
      </span>
    );
  };

  const w = props.width;
  const wide = w >= 1280;
  const mid = w >= 1015;
  const band = wide ? 0 : mid ? 1 : 2;
  const columns: ISetupColumn<IUser>[] = [
    {
      name: "Nome utente",
      width: [200, 180, 170][band],
      render: (u) => (
        <>
          <span className="el" style={{ fontWeight: 600 }}>
            {u.identifier}
            {isMe(u) && (
              <span className="sub" style={{ fontWeight: 400 }}>
                {" "}
                (tu)
              </span>
            )}
          </span>
          <span className="sub el">
            {roleText(u)}
            {u.enabled ? "" : " · spento"}
          </span>
        </>
      ),
    },
    { name: "Email", width: [280, 220, 170][band], render: mailCell },
    { name: "Email degli allarmi", width: [150, 150, 140][band], render: mailPill },
    { name: "Assegnato a", width: [260, 220, 180][band], grow: true, render: caresCell },
    { name: "Attivo", width: 100, hide: !wide, render: (u) => <StateText on={!!u.enabled} /> },
    { name: "", width: 130, render: (u) => <Btn text="Modifica" onClick={() => setEditing(u)} /> },
  ];

  const card = (u: IUser) => (
    <div className="cd">
      <div className="h">
        <span className="el">{u.identifier}</span>
        {isMe(u) && <span className="sub">(tu)</span>}
        {(u.roles ?? []).map((r) => (
          <Pill key={r.id} kind="grey" text={r.type.toUpperCase()} />
        ))}
        {!u.enabled && <Pill kind="grey" text="Spento" />}
      </div>
      <div className="r">
        <Btn text="Modifica" onClick={() => setEditing(u)} />
      </div>
      <div className="l">
        <div className="kv">
          <span className="k">Email</span>
          <span>{mailCell(u)}</span>
          <span className="k">Allarmi</span>
          <span>{mailPill(u)}</span>
          <span className="k">Assegnato a</span>
          <span>{caresCell(u)}</span>
        </div>
      </div>
    </div>
  );

  const empty = (
    <Empty
      title={q ? "Nessun utente per «" + search.trim() + "»" : "Tutti ricevono le email"}
      text={q ? "Cerca per nome utente o email." : "Ogni utente attivo ha un'email e le notifiche accese."}
      action={
        q ? (
          <Btn text="Cancella la ricerca" onClick={() => setSearch("")} />
        ) : (
          <Btn text="Mostra tutti" onClick={() => setFilter("tutti")} />
        )
      }
    />
  );

  // elenco dei dispositivi di un operatore, letti e varchi in ordine di numero
  const caresDialog = () => {
    if (!caresOf) return null;
    const list = devicesOf(caresOf);
    const group = (k: "Letto" | "Varco") =>
      list
        .filter((s) => kindOf(s) === k)
        .map(dispName)
        .sort((a, b) => a.localeCompare(b, "it", { numeric: true }));
    const col = (k: "Letto" | "Varco", t: string) =>
      group(k).length > 0 ? (
        <div>
          <div className="st" style={{ fontWeight: 600, marginBottom: 6 }}>
            {t}{" "}
            <span className="sub" style={{ fontWeight: 400 }}>
              {group(k).length}
            </span>
          </div>
          <ul className="cares-list">
            {group(k).map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </div>
      ) : null;
    const title = "Dispositivi assegnati a " + caresOf.identifier + " (" + list.length + ")";
    return (
      <Dialog title={title} ariaTitle={title} wide onClose={() => setCaresOf(undefined)} footer={<Btn text="Chiudi" primary onClick={() => setCaresOf(undefined)} />}>
        <div className="cares-cols">
          {col("Letto", "Letti")}
          {col("Varco", "Varchi")}
        </div>
        <p className="sub" style={{ margin: 0 }}>
          Si cambiano dal Dispositivo, con la matita della colonna Operatori.
        </p>
      </Dialog>
    );
  };

  return (
    <div>
      <PageHead title="Utenti" line="Chi entra nel Caregiver, con quali ruoli, e le email dove riceve gli allarmi." />
      <Toolbar
        left={
          <>
            <Btn text="Aggiungi utente" icon="Add" primary onClick={() => setEditing(null)} />
            <Chips
              chips={[
                { key: "tutti", text: "Tutti (" + props.users.length + ")" },
                { key: "nonricevono", text: "Non ricevono email (" + props.users.filter(mailReason).length + ")" },
              ]}
              selected={filter}
              onChange={setFilter}
            />
          </>
        }
        shown={rows.length}
        total={props.users.length}
        noun="utenti"
        search={search}
        onSearch={setSearch}
        placeholder="Cerca utente o email"
      />
      {!props.loaded ? (
        <div className="empty">
          <span className="spinner" />
        </div>
      ) : w < CARDS_MAX_WIDTH ? (
        <SetupCards rows={rows} rowKey={(u) => u.id} card={card} empty={empty} />
      ) : (
        <SetupTable columns={columns} rows={rows} rowKey={(u) => u.id} empty={empty} />
      )}
      {editing !== undefined && (
        <UserDialog
          user={editing ?? undefined}
          users={props.users}
          roles={props.roles}
          isMe={!!editing && isMe(editing)}
          onClose={() => setEditing(undefined)}
          onSaved={(message: string, relogin: boolean) => {
            setEditing(undefined);
            if (relogin) {
              props.onRelogin();
              return;
            }
            ToastMessage(message.includes("non sono stati") ? "warning" : "success", message);
            props.reloadUsers();
          }}
        />
      )}
      {caresDialog()}
    </div>
  );
};

export default UsersTab;
